import { useEffect, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { Container } from "react-bootstrap";
import {
  FaBriefcase,
  FaTrashAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import DateInput from "@/components/shared/forms/DateInput";
import TextInput from "@/components/shared/forms/TextInput";
import Select from "@/components/shared/forms/Select";
import { type PRGM } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { usePRGMFormLogic } from "@/components/records/child/usePRGMFormLogic";

const HOMELESS_PROGRAM_CODE = "191";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type PRGMWizardFormProps = {
  form: UseFormReturn<PRGM>;
  preschools: PreschoolSummary[];
  prgmNumber?: number;
  onRemove?: () => void;
};

export function PRGMWizardForm({
  form,
  preschools,
  prgmNumber = 1,
  onRemove,
}: PRGMWizardFormProps) {
  const coreData = useCoreData();
  const leaContext = useRouteContext({ from: "/$leaid" });
  const tooltipMap = leaContext.coreData.tooltipMap.get("PRGM");

  const {
    register,
    control,
    trigger,
    getValues,
    formState: { errors },
  } = form;

  const watchCSPPID = useWatch({ control, name: "CSPPID" });
  const watchLocalChildId = useWatch({ control, name: "LocalChildId" });
  const watchProgramYear = useWatch({ control, name: "ProgramYear" });
  const eduProgramCode = useWatch({ control, name: "EduProgramCode" });
  const membershipStartDate = useWatch({
    control,
    name: "EduProgramMembershipStartDate",
  });

  const { validateMembershipStart, validateMembershipEnd } =
    usePRGMFormLogic(control);

  const selectedProgramText = coreData.educationalProgramList.find(
    (x) => x.Value === eduProgramCode
  )?.Text;

  const [collapsed, setCollapsed] = useState(false);

  const hasErrors = Object.keys(errors).length > 0;
  useEffect(() => {
    if (hasErrors) setCollapsed(false);
  }, [hasErrors]);

  useEffect(() => {
    void trigger(["HomelessDwellingTypeCode", "UnaccompaniedYouthIndicator"]);
  }, [eduProgramCode, trigger]);

  useEffect(() => {
    void trigger("EduProgramMembershipEndDate");
  }, [membershipStartDate, trigger]);

  useEffect(() => {
    void trigger([
      "EduProgramMembershipStartDate",
      "EduProgramMembershipEndDate",
    ]);
  }, [watchProgramYear, trigger]);

  const headerStyle = {
    backgroundColor: "#F9FAFB",
    padding: "14px 20px",
    cursor: "pointer",
    userSelect: "none" as const,
  };

  const Wrapper = prgmNumber === 1 ? Container : "div";
  const wrapperProps =
    prgmNumber === 1
      ? {
          className: "ps-3 pe-3 overview-container",
          style: { borderTop: "none", borderRadius: 0 } as React.CSSProperties,
        }
      : {
          className: "ps-3 pe-3",
        };

  return (
    <Wrapper {...wrapperProps}>
      <div
        className="form-card-modern mb-3"
        style={{
          overflow: "hidden",
          padding: 0,
          ...(onRemove
            ? { border: "2px dashed #c5ccd4", borderRadius: "8px" }
            : {}),
        }}
      >
        <div
          className="section-header d-flex justify-content-between align-items-center"
          style={{
            ...headerStyle,
            width: "100%",
            ...(collapsed ? { marginBottom: 0 } : {}),
          }}
        >
          <button
            type="button"
            className="d-flex align-items-center gap-2 flex-grow-1"
            style={{
              border: "none",
              background: "none",
              font: "inherit",
              textAlign: "left" as const,
              padding: 0,
            }}
            onClick={() => setCollapsed((prev) => !prev)}
            aria-expanded={!collapsed}
          >
            <FaBriefcase className="section-icon" />
            <h3 className="section-title mb-0">
              {prgmNumber === 1
                ? "Education Program"
                : `Education Program ${prgmNumber}`}
            </h3>
          </button>
          <div className="d-flex align-items-center gap-2">
            {onRemove && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{
                  color: "#dc3545",
                  border: "none",
                  background: "transparent",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
                onClick={onRemove}
              >
                <FaTrashAlt style={{ fontSize: "12px" }} />
                Remove
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center"
              style={{ border: "none", background: "transparent", padding: 0 }}
              onClick={() => setCollapsed((prev) => !prev)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? "Expand section" : "Collapse section"}
            >
              {collapsed ? (
                <FaChevronDown style={{ fontSize: "12px", color: "#6c757d" }} />
              ) : (
                <FaChevronUp style={{ fontSize: "12px", color: "#6c757d" }} />
              )}
            </button>
          </div>
        </div>

        {!collapsed && (
          <div style={{ padding: "20px" }}>
            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="EduProgramCode"
                  labelText="Education Program"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      {...register(name, {
                        required: "Education Program is required",
                      })}
                      options={coreData.educationalProgramList}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-8">
                <span
                  className="col-form-label invisible d-block"
                  aria-hidden="true"
                >
                  Education Program
                </span>
                <p className="text-muted mb-4">
                  {selectedProgramText
                    ? `This is a placeholder for some text that will give more information about the requirements for the ${selectedProgramText} program.`
                    : "Select an Education Program to see more information about its requirements."}
                </p>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="ProgramYear"
                  labelText="Program Year"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <ReadOnlySelect
                      id={id}
                      control={control}
                      name={name}
                      rules={{ required: "Program Year is required" }}
                      options={coreData.programYearList}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="PreschoolCode"
                  labelText="School"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <ReadOnlySelect
                      id={id}
                      control={control}
                      name={name}
                      rules={{ required: "School is required" }}
                      options={preschools.map((x) => ({
                        Text: x.PreschoolName,
                        Value: x.PreschoolCDSCode,
                      }))}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="CSPPID"
                  labelText="CSPPID"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      readOnly
                      value={watchCSPPID}
                      {...register(name)}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="LocalChildId"
                  labelText="Local Child ID (CICN)"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      readOnly
                      value={watchLocalChildId}
                      {...register(name)}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="EduProgramMembershipStartDate"
                  labelText="Membership Start Date"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <DateInput
                      id={id}
                      max="9999-12-31"
                      {...register(name, {
                        required: "Membership Start Date is required",
                        validate: validateMembershipStart,
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="EduProgramMembershipEndDate"
                  labelText="Membership End Date"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <DateInput
                      id={id}
                      max="9999-12-31"
                      {...register(name, {
                        validate: validateMembershipEnd,
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="HomelessDwellingTypeCode"
                  labelText="Homeless Dwelling Type"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired={eduProgramCode === HOMELESS_PROGRAM_CODE}
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      {...register(name, {
                        validate: (v) => {
                          if (
                            getValues("EduProgramCode") ===
                              HOMELESS_PROGRAM_CODE &&
                            !v
                          ) {
                            return "Homeless Dwelling Type is required when Education Program is Homeless Program (191)";
                          }
                          return true;
                        },
                      })}
                      options={coreData.homelessDwellingTypeCodeList}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="UnaccompaniedYouthIndicator"
                  labelText="Unaccompanied Youth Indicator"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired={eduProgramCode === HOMELESS_PROGRAM_CODE}
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      {...register(name, {
                        validate: (v) => {
                          if (
                            getValues("EduProgramCode") ===
                              HOMELESS_PROGRAM_CODE &&
                            !v
                          ) {
                            return "Unaccompanied Youth Indicator is required when Education Program is Homeless Program (191)";
                          }
                          return true;
                        },
                      })}
                      options={[
                        { Text: "Yes", Value: "Y" },
                        { Text: "No", Value: "N" },
                      ]}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
